"use client";

import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { getExplainer } from "@/lib/api/explain";
import type {
  ExplainerResponse,
  ExplainerCTA,
  ExplainerIntensityKey,
  ExplainerType,
} from "@/lib/api/explain";
import { trackEvent } from "@/lib/analytics";
import { submitFeedback } from "@/lib/api/feedback";

/**
 * Lazy fetcher for /v1/explain/{type}/{id}. Keep this hook on the web
 * app's React Query import so it reads the same QueryClientProvider
 * instance that layout.tsx installs.
 *
 * `enabled` defaults to false: the network call fires only when the user
 * opens the sheet, keeping report pages fast on load.
 */
export function useExplainer(
  type_: ExplainerType,
  id: string,
  intensity?: ExplainerIntensityKey,
  options?: { enabled?: boolean }
) {
  return useQuery<ExplainerResponse>({
    queryKey: ["explainer", type_, id, intensity ?? null] as const,
    queryFn: () => getExplainer(type_, id, intensity),
    enabled: options?.enabled ?? false,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Shared track + cta-press helpers wired into <Explainable> on web.
 *
 * Pass through to <Explainable onTrack={...} onCtaPress={...}/> so
 * every consumer page gets PostHog + same-origin navigation for free.
 */
export function useExplainableHandlers(surface: string) {
  const onTrack = useCallback((event: string, props: Record<string, unknown>) => {
    // Event names match the auto-generated mobile enum and live as
    // string literals on web — adding to AnalyticsEvent here too keeps
    // PostHog's event registry consistent across surfaces.
    trackEvent(event, props);
  }, []);

  const onCtaPress = useCallback((cta: ExplainerCTA) => {
    // Deep-links are ishvaram:// on mobile; web maps via a route prefix.
    const webPath = cta.deep_link.replace("ishvaram://", "/");
    if (typeof globalThis.window !== "undefined") {
      globalThis.window.location.assign(webPath);
    }
  }, []);

  const onFeedback = useCallback(
    (helpful: boolean) => {
      void submitFeedback({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        source: "explainable_thumb" as any,
        rating: helpful ? 1 : 0,
        context: {
          surface,
          helpful,
          path: typeof window !== "undefined" ? window.location.pathname : undefined,
        },
      });
    },
    [surface]
  );

  return { surface, onTrack, onCtaPress, onFeedback };
}
